import type React from "react"
import { useState, useEffect } from "react"
import { Typography, Space, Button, Select, Input, Radio, Empty, Card, Checkbox, Tag, Tooltip, Spin } from "antd"
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  BulbOutlined,
  WarningOutlined,
  ArrowRightOutlined,
  UndoOutlined,
  ThunderboltOutlined,
  EditOutlined,
} from "@ant-design/icons"
import type { BusinessArea, Tag as TagType } from "types/components/IAITagGenerationWizard"
const { Title, Text, Paragraph } = Typography
const { Option } = Select
const { TextArea } = Input

interface ReviewStepProps {
  generatedTags: TagType[]
  selectedBusinessArea: string
  businessAreaList: BusinessArea[]
  searchText: string
  viewMode: "list" | "grid"
  handleBusinessAreaChange: (value: string) => void
  handleTagSelection: (index: number) => void
  handleSelectAll: () => void
  handleDeselectAll: () => void
  setSearchText: (text: string) => void
  setViewMode: (mode: "list" | "grid") => void
  handleRephraseDescription?: (index: number) => Promise<void>
  handleRevertDescription?: (index: number) => void
  handleUpdateDescription?: (index: number, description: string) => void
  isRephrasing?: boolean
  currentRephrasingTagIndex?: number | null
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  generatedTags,
  selectedBusinessArea,
  businessAreaList,
  searchText,
  viewMode,
  handleBusinessAreaChange,
  handleTagSelection,
  handleSelectAll,
  handleDeselectAll,
  setSearchText,
  setViewMode,
  handleRephraseDescription,
  handleRevertDescription,
  handleUpdateDescription,
  isRephrasing,
  currentRephrasingTagIndex,
}) => {
  const [showFullDescriptionIndex, setShowFullDescriptionIndex] = useState<number | null>(null)
  const [editingDescriptionIndex, setEditingDescriptionIndex] = useState<number | null>(null)
  const [editedDescription, setEditedDescription] = useState<string>("")

  useEffect(() => {
    setShowFullDescriptionIndex(null)
  }, [searchText])

  const filteredTags = generatedTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(searchText.toLowerCase()) ||
      tag.description.toLowerCase().includes(searchText.toLowerCase()),
  )
  const getOriginalIndex = (filteredIndex: number) => {
    if (filteredIndex < 0 || filteredIndex >= filteredTags.length) return -1
    const targetTag = filteredTags[filteredIndex]
    return generatedTags.findIndex((tag) => tag.name === targetTag.name)
  }

  const getFilteredIndex = (originalIndex: number) => {
    if (originalIndex < 0 || originalIndex >= generatedTags.length) return -1
    const targetTag = generatedTags[originalIndex]
    return filteredTags.findIndex((tag) => tag.name === targetTag.name)
  }

  const startEditing = (filteredIndex: number) => {
    const originalIndex = getOriginalIndex(filteredIndex)
    if (originalIndex >= 0) {
      setEditingDescriptionIndex(filteredIndex)
      setEditedDescription(generatedTags[originalIndex].description)
    }
  }

  const saveDescription = () => {
    if (editingDescriptionIndex !== null) {
      const originalIndex = getOriginalIndex(editingDescriptionIndex)
      if (originalIndex >= 0 && handleUpdateDescription) {
        handleUpdateDescription(originalIndex, editedDescription)
      }
      setEditingDescriptionIndex(null)
      setEditedDescription("")
    }
  }

  const cancelEditing = () => {
    setEditingDescriptionIndex(null)
    setEditedDescription("")
  }
  const isTagRephrasing = (originalIndex: number) => {
    return isRephrasing && currentRephrasingTagIndex === originalIndex
  }
  const renderDescription = (tag: TagType, filteredIndex: number) => {
    const originalIndex = getOriginalIndex(filteredIndex)
    const isEditing = editingDescriptionIndex === filteredIndex
    const isCurrentlyRephrasing = isTagRephrasing(originalIndex)
    if (isEditing) {
      return (
        <div className="relative mt-2 border border-blue-200 rounded-md p-2 bg-blue-50">
          <div className="absolute top-2 right-2 flex gap-1 mb-3">
            <Button
              size="small"
              type="primary"
              onClick={(e) => {
                e.stopPropagation()
                saveDescription()
              }}
              className="flex items-center"
              title="Save changes"
            >
              <CheckCircleOutlined />
            </Button>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                cancelEditing()
              }}
              className="flex items-center"
              title="Cancel editing"
            >
              <CloseCircleOutlined />
            </Button>
          </div>
          <TextArea
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
            className="mb-2 mt-8"
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        </div>
      )
    }

    if (isCurrentlyRephrasing) {
      return (
        <div className="flex items-center gap-2 py-2 mt-1">
          <Spin size="small" />
          <Text type="secondary" italic>
            Rephrasing description with AI...
          </Text>
        </div>
      )
    }

    const showFullDescription = showFullDescriptionIndex === filteredIndex

    return (
      <div className="relative">
        <div className="absolute top-0 right-0 my">
          <Tooltip title="Edit description">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              className="text-gray-400 hover:text-blue-500"
              onClick={(e) => {
                e.stopPropagation()
                startEditing(filteredIndex)
              }}
              disabled={isRephrasing}
            />
          </Tooltip>
        </div>

        {showFullDescription ? (
          <div className="mt-1 pr-6">
            <Paragraph className="text-sm text-gray-600 m-0 whitespace-pre-wrap">{tag.description}</Paragraph>
            <div className="flex flex-wrap gap-2 mt-2">
              <Tooltip title="Show less text">
                <Button
                  type="link"
                  size="small"
                  className="p-0 h-auto text-xs"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowFullDescriptionIndex(null)
                  }}
                >
                  Show less
                </Button>
              </Tooltip>

              {handleRephraseDescription && (
                <Tooltip title="Use AI to improve this description">
                  <Button
                    type="link"
                    size="small"
                    icon={<ThunderboltOutlined />}
                    className="p-0 h-auto text-xs font-extrabold text-blue-500"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRephraseDescription(originalIndex)
                    }}
                    disabled={isRephrasing}
                  >
                    {isRephrasing ? "Rephrasing..." : "Rephrase with AI"}
                  </Button>
                </Tooltip>
              )}

              {tag.originalDescription && handleRevertDescription && (
                <Tooltip title="Restore original description">
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<UndoOutlined />}
                    className="p-0 h-auto text-xs font-bold"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRevertDescription(originalIndex)
                    }}
                  >
                    Revert
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-1 pr-6">
            <Paragraph className="text-sm text-gray-600 m-0" ellipsis={{ rows: 2 }}>
              {tag.description}
            </Paragraph>
            <div className="flex flex-wrap gap-2 mt-1">
              <Tooltip title="Show full description">
                <Button
                  type="link"
                  size="small"
                  className="p-0 h-auto text-xs"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowFullDescriptionIndex(filteredIndex)
                  }}
                >
                  Show more
                </Button>
              </Tooltip>

              {handleRephraseDescription && (
                <Tooltip title="Use AI to improve this description">
                  <Button
                    type="link"
                    size="small"
                    icon={<ThunderboltOutlined />}
                    className="p-0 h-auto text-xs font-extrabold text-blue-500"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRephraseDescription(originalIndex)
                    }}
                    disabled={isRephrasing}
                  >
                    {isRephrasing ? "Rephrasing..." : "Rephrase with AI"}
                  </Button>
                </Tooltip>
              )}
               {tag.originalDescription && handleRevertDescription && (
                <Tooltip title="Restore original description">
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<UndoOutlined />}
                    className="p-0 h-auto text-xs font-bold"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRevertDescription(originalIndex)
                    }}
                  >
                    Revert
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-5 mt-4">
      <div className="flex justify-between items-center mb-5">
        <Title level={4}>Review Generated Tags</Title>
        <Space className="flex-nowrap">
          <Button
            size="small"
            onClick={handleSelectAll}
            type="primary"
            ghost
            icon={<CheckCircleOutlined />}
            className="flex items-center"
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={handleDeselectAll}
            danger
            ghost
            icon={<CloseCircleOutlined />}
            className="flex items-center"
          >
            Deselect All
          </Button>
        </Space>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div>
          <Text strong>Business Area</Text>
          <Select
            className="w-full mt-2"
            value={selectedBusinessArea}
            onChange={handleBusinessAreaChange}
            placeholder="Select business area"
            aria-label="Select business area"
          >
            {businessAreaList.map((area) => (
              <Option key={area.businessAreaId} value={area.businessAreaId}>
                {area.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" className="block mt-1 text-xs">
            You can change the business area for these tags
          </Text>
        </div>
        <div>
          <Text strong>Search Tags</Text>
          <Input
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search by name or description"
            className="w-full mt-2"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            aria-label="Search tags"
          />
          <div className="flex justify-between mt-1">
            <Text type="secondary" className="text-xs">
              {filteredTags.length} of {generatedTags.length} tags shown
            </Text>
            <div>
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                size="small"
                buttonStyle="solid"
                aria-label="Change view mode"
              >
                <Radio.Button value="list" aria-label="List view">
                  <UnorderedListOutlined />
                </Radio.Button>
                <Radio.Button value="grid" aria-label="Grid view">
                  <AppstoreOutlined />
                </Radio.Button>
              </Radio.Group>
            </div>
          </div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden shadow-sm bg-white">
        <div className="bg-blue-50 p-3 border-b flex justify-between items-center">
          <Text strong>
            <CheckCircleOutlined className="text-green-500 mr-1" />
            {generatedTags.filter((t) => t.selected).length} of {generatedTags.length} tags selected
          </Text>
          <Text type="secondary" className="text-sm">
            Click on a tag to select/deselect
          </Text>
        </div>

        {filteredTags.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No tags match your search criteria"
            className="py-10"
          />
        ) : viewMode === "list" ? (
          <div className="max-h-[320px] overflow-y-auto">
            {filteredTags.map((tag, filteredIndex) => {
              const originalIndex = getOriginalIndex(filteredIndex)
              const isCurrentlyRephrasing = isTagRephrasing(originalIndex)
              return (
                <div
                  key={tag.name}
                  className={`border-b last:border-b-0 p-4 hover:bg-gray-50 transition-colors cursor-pointer
                    ${tag.selected ? "border-l-4 border-l-blue-500 pl-3" : "pl-4 border-l-4 border-l-transparent"}
                    ${isCurrentlyRephrasing ? "bg-blue-50" : ""}
                  `}
                  onClick={() => handleTagSelection(originalIndex)}
                  role="checkbox"
                  aria-checked={tag.selected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleTagSelection(originalIndex)
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={tag.selected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleTagSelection(originalIndex)
                      }}
                      className="mt-1 flex-shrink-0"
                    />
                    <div className="flex-grow">
                      <div className="mb-2 flex items-center gap-2">
                        <Tag color={tag.isDuplicate ? "orange" : "blue"} className="text-sm px-2 py-1 font-medium">
                          {tag.name}
                        </Tag>
                        {tag.isDuplicate && (
                          <Tooltip
                            title={
                              <div>
                                <div className="mb-1">Renamed to avoid duplicate with existing Tag</div>
                                <div className="flex items-center gap-2">
                                  <span className="line-through text-gray-400">{tag.originalName}</span>
                                  <ArrowRightOutlined className="text-xs" />
                                  <span className="font-medium">{tag.name}</span>
                                </div>
                              </div>
                            }
                          >
                            <Tag className="flex items-center bg-orange-50 text-orange-600 border-orange-200 m-0 px-1.5">
                              <WarningOutlined className="mr-1 text-sm" />
                              <span className="text-xs font-semibold">Renamed</span>
                            </Tag>
                          </Tooltip>
                        )}
                      </div>
                      {renderDescription(tag, filteredIndex)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 max-h-[320px] overflow-y-auto">
            {filteredTags.map((tag, filteredIndex) => {
              const originalIndex = getOriginalIndex(filteredIndex)
              const isCurrentlyRephrasing = isTagRephrasing(originalIndex)
              return (
                <Card
                  key={tag.name}
                  size="small"
                  hoverable
                  className={`h-full cursor-pointer hover:shadow-md transition-shadow overflow-hidden
          ${tag.selected ? "border-blue-500" : "border-gray-200"}
          ${isCurrentlyRephrasing ? "bg-blue-50" : ""}
        `}
                  onClick={() => handleTagSelection(originalIndex)}
                  role="checkbox"
                  aria-checked={tag.selected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleTagSelection(originalIndex)
                    }
                  }}
                >
                  <div className="flex items-start">
                    <Checkbox
                      checked={tag.selected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleTagSelection(originalIndex)
                      }}
                      className="mt-1 mr-2 flex-shrink-0"
                    />
                    <div className="w-full">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex flex-wrap items-center gap-1 max-w-[85%]">
                          <Tag color={tag.isDuplicate ? "orange" : "blue"} className="text-xs py-1">
                            {tag.name}
                          </Tag>
                          {tag.isDuplicate && (
                            <Tooltip
                              title={
                                <div>
                                  <div className="mb-1">Renamed to avoid duplicate</div>
                                  <div className="flex items-center gap-2">
                                    <span className="line-through text-gray-400">{tag.originalName}</span>
                                    <ArrowRightOutlined className="text-xs" />
                                    <span className="font-medium">{tag.name}</span>
                                  </div>
                                </div>
                              }
                            >
                              <Tag className="flex items-center bg-orange-50 text-orange-600 border-orange-200 m-0 px-1">
                                <WarningOutlined className="text-xs mr-0.5" />
                                <span className="text-xs">Renamed</span>
                              </Tag>
                            </Tooltip>
                          )}
                        </div>
                        <Tooltip title="Edit description">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            className="text-gray-400 hover:text-blue-500 -mt-1 -mr-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              startEditing(filteredIndex)
                            }}
                            disabled={isRephrasing}
                          />
                        </Tooltip>
                      </div>

                      {editingDescriptionIndex === filteredIndex ? (
                        <div className="relative border border-blue-200 rounded-md p-2 bg-blue-50">
                          <TextArea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            className="mb-2"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                          <div className="flex justify-end gap-1">
                            <Button
                              size="small"
                              type="primary"
                              onClick={(e) => {
                                e.stopPropagation()
                                saveDescription()
                              }}
                              className="flex items-center"
                              title="Save changes"
                            >
                              <CheckCircleOutlined />
                            </Button>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                cancelEditing()
                              }}
                              className="flex items-center"
                              title="Cancel editing"
                            >
                              <CloseCircleOutlined />
                            </Button>
                          </div>
                        </div>
                      ) : isCurrentlyRephrasing ? (
                        <div className="flex items-center gap-2 py-1">
                          <Spin size="small" />
                          <Text type="secondary" italic className="text-xs">
                            Rephrasing...
                          </Text>
                        </div>
                      ) : (
                        <>
                          <Paragraph className="text-xs text-gray-600 m-0" ellipsis={{ rows: 3 }}>
                            {tag.description}
                          </Paragraph>

                          <div className="flex flex-wrap gap-1 mt-1">
                            {showFullDescriptionIndex === filteredIndex ? (
                              <Tooltip title="Show less text">
                                <Button
                                  type="link"
                                  size="small"
                                  className="p-0 h-auto text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setShowFullDescriptionIndex(null)
                                  }}
                                >
                                  Show less
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Show full description">
                                <Button
                                  type="link"
                                  size="small"
                                  className="p-0 h-auto text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setShowFullDescriptionIndex(filteredIndex)
                                  }}
                                >
                                  Show more
                                </Button>
                              </Tooltip>
                            )}

                            {handleRephraseDescription && (
                              <Tooltip title="Use AI to improve this description">
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<ThunderboltOutlined className="font-extrabold" />}
                                  className="p-0 h-auto text-xs font-extrabold text-blue-500"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRephraseDescription(originalIndex)
                                  }}
                                  disabled={isRephrasing}
                                >
                                  {isRephrasing ? "Rephrasing..." : ""}
                                </Button>
                              </Tooltip>
                            )}

                            {tag.originalDescription && handleRevertDescription && (
                              <Tooltip title="Restore original description">
                                <Button
                                  type="link"
                                  size="small"
                                  danger
                                  icon={<UndoOutlined  className="font-extrabold"/>}
                                  className="p-0 h-auto text-xs font-bold"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRevertDescription(originalIndex)
                                  }}
                                >
                                  {""}
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 mt-5 bg-blue-50 p-3 rounded-lg">
        <BulbOutlined className="text-blue-500 mt-1 flex-shrink-0" />
        <div>
          <Text strong>Pro Tip</Text>
          <Paragraph className="text-sm text-gray-600 m-0">
            Select only the most relevant tags for your use case. You can always generate more tags later. Use the
            search to quickly find specific tags. The business area you select will be applied to all tags.
          </Paragraph>
        </div>
      </div>
    </div>
  )
}
